import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import DateInput from '../components/DateInput'
import AddressAutocomplete from '../components/AddressAutocomplete'
import { useAuth } from '../context/AuthContext'
import { upsertProfile } from '../lib/profiles'
import {
  saveOrder,
  createPickupGroup,
  computeReturn,
  minReturnDate,
  allowedReturnSlots,
} from '../lib/orders'
import { notifyNewOrder } from '../lib/telegramAlerts'
import { sendBooked } from '../lib/emailTemplate'
import {
  listActiveServices,
  listItemsForService,
  listPricingZones,
  getMemberCycleUsedLbs,
  insertOrderItems,
} from '../lib/catalog'
 import { zoneForZip } from '../lib/serviceArea'
import { computeOrderTotal, fmtMoney } from '../lib/pricing'
import { sendSms } from '../lib/sms'
import { availableSlotsFor, earliestPickupDate, pickupWindowFor, shortDate, SLOTS, addDays, isoDate } from '../lib/dates'
// Spec §6: no booking email. The only customer-facing emails are
// picked_up and delivered, both fired from the admin.
import { showToast } from '../components/Toast'
import { trackBeginCheckout, trackPurchase } from '../lib/adTracking'

// Below this many lbs left in the cycle, the top-up offer appears on the
// booking screen. 15 lbs ≈ one average bag: the next pickup would spill into
// overage, so this is the last moment the offer is still useful.
const LOW_LBS_THRESHOLD = 15

const BAGS = [
  { value: '1', label: '1 bag' },
  { value: '2', label: '2 bags' },
  { value: '3', label: '3 bags' },
  { value: '4+', label: '4+ bags' },
]

// Human blurb for services.turnaround. If the DB row doesn't set turnaround
// text we fall back to a sensible per-mode default.
function turnaroundLabel(service) {
  if (!service) return ''
  // Spec §4: Wash & Press and Dry Cleaning are BOTH "Confirmed when ready".
  if (service.key === 'dry_cleaning' || service.key === 'wash_press') return 'Confirmed when ready'
  if (service.turnaround) return service.turnaround
  if (service.key === 'same_day') return 'tonight'
  return 'in 24–48 hours'
}

function addrFromProfile(p) {
  return {
    line1: p?.address_line1 ?? '',
    line2: p?.address_line2 ?? '',
    city: p?.city ?? '',
    state: p?.state ?? '',
    zip: p?.zip ?? '',
    hotelName: p?.hotel_name ?? '',
    roomNumber: p?.room_number ?? '',
    isHotel: !!p?.hotel_name,
  }
}
function formatSummary(a) {
  const one = a?.line1 || ''
  const two = [a?.city, a?.state].filter(Boolean).join(' ')
  const parts = [one, two, a?.zip].filter(Boolean)
  return parts.join(', ')
}

export default function Order() {
  const navigate = useNavigate()
  const { user, profile, refresh } = useAuth()

  // ── Catalog + membership balance ─────────────────────────────────────
  const [services, setServices] = useState([])
  const [servicesError, setServicesError] = useState('')
  const [memberLbsUsed, setMemberLbsUsed] = useState(0)
  const [addons, setAddons] = useState([])
  const [garments, setGarments] = useState([])

  const [serviceId, setServiceId] = useState(null)
  const service = useMemo(
    () => services.find((s) => s.id === serviceId) || null,
    [services, serviceId],
  )

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const rows = await listActiveServices()
        if (!alive) return
        setServices(rows)
        // Default to wash_fold (or first row)
        const def = rows.find((r) => r.key === 'wash_fold') || rows[0]
        if (def) setServiceId(def.id)
      } catch (err) {
        setServicesError(err?.message || 'Could not load services')
      }
    })()
    return () => { alive = false }
  }, [])

  useEffect(() => {
    if (!user?.id) return
    // No excludeOrderId at booking — the order doesn't exist yet.
    getMemberCycleUsedLbs(user.id).then(setMemberLbsUsed).catch(() => {})
  }, [user?.id])

  // Garments come from two places: the current service's own items
  // (dry_cleaning owns the full list) AND the dry_cleaning service itself,
  // so a Wash & Fold customer can still add a few dry-clean pieces to the
  // same order.
  const dryCleaningServiceId = useMemo(
    () => services.find((s) => s.key === 'dry_cleaning')?.id || null,
    [services],
  )


  // ── Address ──────────────────────────────────────────────────────────
  const [addr, setAddr] = useState(addrFromProfile(profile))
  const [editingAddress, setEditingAddress] = useState(!profile?.address_line1)
  const [saveAsDefault, setSaveAsDefault] = useState(false)
  const [addrNote, setAddrNote] = useState({ text: '', warn: false })
  const setAddrField = (k, v) => setAddr((cur) => ({ ...cur, [k]: v }))

  useEffect(() => {
    if (editingAddress) return
    setAddr(addrFromProfile(profile))
    if (!profile?.address_line1) setEditingAddress(true)
  }, [profile, editingAddress])

  useEffect(() => {
    if (!addrNote.text) return
    const t = setTimeout(() => setAddrNote({ text: '', warn: false }), 6000)
    return () => clearTimeout(t)
  }, [addrNote])

  // Picking a hotel OVERWRITES the address (same rule as signup).
  function handleHotelPick(p) {
    const prevStreet = addr.line1.trim()
    setAddr((cur) => ({
      ...cur,
      hotelName: p.hotel,
      isHotel: true,
      line1: p.street,
      city: p.city,
      state: p.state,
      zip: p.zip,
    }))
    setAddrNote({
      text: prevStreet && prevStreet !== p.street
        ? `Address replaced by the one on file for ${p.hotel}.`
        : `Address filled in from ${p.hotel}.`,
      warn: false,
    })
  }

  // Picking an address never replaces an already-typed hotel name.
  function handleAddressPick(p) {
    setAddr((cur) => {
      const next = { ...cur, line1: p.street, city: p.city, state: p.state, zip: p.zip }
      if (p.hotel && cur.isHotel && !cur.hotelName.trim()) {
        next.hotelName = p.hotel
        setAddrNote({ text: `${p.hotel} added — clear it if you're not there.`, warn: false })
      } else if (p.hotel && cur.hotelName.trim() && cur.hotelName.trim() !== p.hotel) {
        setAddrNote({
          text: `Heads up: you wrote "${cur.hotelName.trim()}" but this address is ${p.hotel}.`,
          warn: true,
        })
      } else {
        setAddrNote({ text: '', warn: false })
      }
      return next
    })
  }

  // ── Pricing zone ─────────────────────────────────────────────────────
  // Derived from the PICKUP address ZIP — one list, src/lib/serviceArea.js.
  // An unknown ZIP falls to the default zone: normal prices, no error, and
  // no dry cleaning, because nobody could actually clean it.
  const zone = useMemo(() => zoneForZip(addr.zip), [addr.zip])
  const [zones, setZones] = useState([])
  useEffect(() => { listPricingZones().then(setZones).catch(() => setZones([])) }, [])
  const dryAvailable = useMemo(() => {
    const z = zones.find((x) => x.key === zone)
    // Until the zone list has loaded we assume NOT available: showing a
    // service and taking it away a second later is worse than a short wait.
    return z ? !!z.dry_cleaning : false
  }, [zones, zone])

  // The catalog depends on the zone, so it is declared after it: a
  // dependency array is read during render, before anything below it.
  useEffect(() => {
    if (!serviceId) { setAddons([]); setGarments([]); return }
    let alive = true
    const ownGarmentsP = listItemsForService(serviceId, { kind: 'garment', zone })
    // Dry-clean garments only ride along where dry cleaning exists.
    const dryGarmentsP = dryAvailable && dryCleaningServiceId && dryCleaningServiceId !== serviceId
      ? listItemsForService(dryCleaningServiceId, { kind: 'garment', zone })
      : Promise.resolve([])
    Promise.all([
      listItemsForService(serviceId, { kind: 'addon', zone }),
      ownGarmentsP,
      dryGarmentsP,
    ]).then(([a, own, dry]) => {
      if (!alive) return
      setAddons(a)
      const seen = new Set()
      const merged = [...own, ...dry].filter((g) => {
        if (seen.has(g.id)) return false
        seen.add(g.id); return true
      })
      setGarments(merged)
    }).catch(() => {})
    return () => { alive = false }
  }, [serviceId, dryCleaningServiceId, zone, dryAvailable])

  // ── Booking state ────────────────────────────────────────────────────
  const earliest = useMemo(() => earliestPickupDate(), [])
  const [bags, setBags] = useState('1')
  const [pickupDate, setPickupDate] = useState(earliest)
  const [pickupSlot, setPickupSlot] = useState('')
  const [notes, setNotes] = useState('')

  const [returnOverride, setReturnOverride] = useState(null)
  const [editingReturn, setEditingReturn] = useState(false)

  // Reaching this screen is the start of a checkout, whatever they do next.
  useEffect(() => { trackBeginCheckout() }, [])

  const [addonQty, setAddonQty] = useState({}) // itemId → 0/1
  const [garmentQty, setGarmentQty] = useState({}) // itemId → n

  const [dryOpen, setDryOpen] = useState(false)

  // Auto-open dry cleaning when the customer's chosen service IS dry cleaning.
  useEffect(() => {
    if (!service) return
    if (service.key === 'dry_cleaning') setDryOpen(true)
  }, [service])

  // Reset extras when service changes (different addon/garment lists).
  useEffect(() => {
    setAddonQty({})
    setGarmentQty({})
  }, [serviceId])

  // ── The pickup is composed service by service ───────────────────────
  // "Add service" drops the current selection into this cart, then the form
  // resets for the next one. Each entry becomes its OWN order: separate flat
  // rate, separate return, separate ticket — tied together server-side by a
  // pickup_group_id. Max 3, and a service can only be added once.
  const [cart, setCart] = useState([]) // [{ key, service, bags, lines, quote }]

  // 2+ services on one pickup ⇒ everything comes back in a single trip, so
  // the whole group returns TBD. Visible BEFORE the customer books.
  const multiService = cart.length >= 2
  const cartFull = cart.length >= 3
  const cartTotalCents = cart.reduce((s, c) => s + Number(c.quote?.totalCents || 0), 0)

  const removeFromCart = (key) => setCart((cur) => cur.filter((c) => c.key !== key))

  // Reset return override when pickup shifts or the cart changes.
  useEffect(() => {
    setReturnOverride(null)
    setEditingReturn(false)
  }, [cart, pickupDate, pickupSlot])

  const pickupSlots = useMemo(() => availableSlotsFor(pickupDate), [pickupDate])
  useEffect(() => {
    if (pickupSlot && !pickupSlots.find((s) => s.id === pickupSlot)) setPickupSlot('')
  }, [pickupSlot, pickupSlots])

  // Same-Day bounds the pickup date to today/tomorrow. Driven by the CART,
  // not by whatever is selected in the form: the date belongs to the pickup,
  // and the cart is what gets booked.
  const cartHasSameDay = cart.some((c) => c.service?.key === 'same_day')
  const pickupWindow = useMemo(
    () => pickupWindowFor(cartHasSameDay ? 'same_day' : null),
    [cartHasSameDay],
  )

  // Adding Same-Day to a pickup already dated three weeks out has to pull
  // the date back, or the guard would only catch it at Book now — after the
  // customer has stopped looking at the date.
  useEffect(() => {
    if (!pickupDate) return
    if (pickupWindow.max && pickupDate > pickupWindow.max) setPickupDate(pickupWindow.max)
    else if (pickupDate < pickupWindow.min) setPickupDate(pickupWindow.min)
  }, [pickupWindow, pickupDate])

  // The return is a property of the pickup, not of whatever is selected in the
  // form right now. With one service it follows that service's turnaround;
  // with several, everything comes back in one trip once all of it is done.
  const returnService = cart.length === 1 ? cart[0].service : null
  const autoReturn = useMemo(
    () => computeReturn(returnService?.key, pickupDate, pickupSlot),
    [returnService?.key, pickupDate, pickupSlot],
  )
  const effectiveReturn = returnOverride
    ? {
        date: returnOverride.date,
        slot: returnOverride.slot,
        deferred: false,
        label: `${shortDate(returnOverride.date)} · ${SLOTS[returnOverride.slot]?.sub || ''}`,
      }
    : autoReturn

  const isMember = !!profile?.is_member
  const membersGate = service?.key === 'members' && !isMember

  // "Running low" notice, shown once the cycle is nearly spent. The rate
  // comes from the services row — never hard-coded here.
  const membersService = services.find((s) => s.key === 'members') || null
  const memberLbsLeft = membersService
    ? Math.max(0, Math.round(
        ((Number(membersService.monthly_included_lbs) || 0) - (Number(memberLbsUsed) || 0)) * 10,
      ) / 10)
    : null
  const lowOnLbs = isMember && memberLbsLeft != null && memberLbsLeft <= LOW_LBS_THRESHOLD
  const overagePerLb = (Number(membersService?.extra_lb_cents) || 0) / 100

  // Members land on their plan's wash & fold. The generic Wash & Fold default
  // is how an active member ended up booked — and billed — outside their plan.
  // `userPickedService` keeps this from overriding a deliberate choice,
  // except when that choice is plain Wash & Fold, which members can't see
  // (the profile can load after the customer has already tapped a row).
  const userPickedService = useRef(false)
  useEffect(() => {
    if (!isMember || services.length === 0) return
    const plan = services.find((s) => s.key === 'members')
    if (!plan) return
    const current = services.find((s) => s.id === serviceId)
    if (!userPickedService.current || current?.key === 'wash_fold') setServiceId(plan.id)
  }, [isMember, services, serviceId])

  // Active members never see plain Wash & Fold: their plan covers it and even
  // their overage rate is below Wash & Fold's, so picking it could only cost
  // them money. Their plan's row goes first instead.
  const orderedServices = isMember
    ? [
        ...services.filter((s) => s.key === 'members'),
        ...services.filter((s) => s.key !== 'members' && s.key !== 'wash_fold'),
      ]
    : services
  // A service already added to the pickup can't be added twice — and a
  // service this address can't actually receive is never shown at all. No
  // greyed-out row, no "unavailable in your area": invisible. A service you
  // cannot order has no business being on screen.
  const visibleServices = orderedServices.filter(
    (s) => !cart.some((c) => c.service?.id === s.id)
      && (dryAvailable || s.key !== 'dry_cleaning'),
  )

  // The address changed and the new one has no dry cleaning: pull those
  // items back out of the pickup and say so. Only ever touches the order
  // being composed — nothing already booked is reprised.
  const dryNoticeShown = useRef(false)
  useEffect(() => {
    if (dryAvailable || zones.length === 0) { dryNoticeShown.current = false; return }
    const hadDry = cart.some((c) => c.service?.key === 'dry_cleaning')
    const selectedDry = service?.key === 'dry_cleaning'
    if (!hadDry && !selectedDry) return
    if (hadDry) setCart((cur) => cur.filter((c) => c.service?.key !== 'dry_cleaning'))
    if (selectedDry) setServiceId(null)
    if (!dryNoticeShown.current) {
      dryNoticeShown.current = true
      showToast("Dry cleaning isn't available at this address — we removed those items.")
    }
  }, [dryAvailable, zones.length, cart, service?.key])

  // ── Build order_items lines from chosen extras ───────────────────────
  const lines = useMemo(() => {
    const rows = []
    for (const a of addons) {
      const q = addonQty[a.id] || 0
      if (q > 0) rows.push({
        item_id: a.id,
        name: a.name,
        price_cents: a.price_cents,
        unit: a.unit,
        quantity: q,
      })
    }
    for (const g of garments) {
      const q = garmentQty[g.id] || 0
      if (q > 0) rows.push({
        item_id: g.id,
        name: g.name,
        price_cents: g.price_cents,
        unit: g.unit,
        quantity: q,
      })
    }
    return rows
  }, [addons, garments, addonQty, garmentQty])

  // Live estimate — pre-weigh, so we pass the service's included_lbs as
  // the "typical" weight for per_pound (shows the flat forfait), 0 for
  // members (within quota) and per_item (only lines matter).
  const estimate = useMemo(() => {
    if (!service) return null
    const weighedLbs =
      service.pricing_mode === 'per_pound' ? (Number(service.included_lbs) || 0) : 0
    // catalog.js rows carry `price_cents` not `unit_price_cents` — normalise
    // so pricing.js reads a consistent shape.
    const items = lines.map((l) => ({
      unit: l.unit,
      unit_price_cents: l.price_cents,
      quantity: l.quantity,
      name_snapshot: l.name,
      item_id: l.item_id,
    }))
    return {
      lines: items,
      quote: computeOrderTotal({
        service,
        weighedLbs,
        bags,
        items,
        memberLbsUsedThisMonth: memberLbsUsed,
      }),
    }
  }, [service, lines, bags, memberLbsUsed])

  // A service that is only SELECTED is not in the pickup yet, so it never
  // counts towards the total and never shows up as a line. The customer must
  // add it or drop it first — `hasPending` is what gates Book now.
  const hasPending = !!service
  const barTotalCents = cart.length > 0
    ? cartTotalCents
    : Number(estimate?.quote?.totalCents || 0)

  // One breakdown section per service actually in the pickup. While the
  // pickup is still empty the selection previews itself — there is nothing
  // else to show and no total it could inflate.
  const breakdownSections = (
    cart.length > 0
      ? cart.map((c) => ({ key: c.key, service: c.service, quote: c.quote }))
      : estimate?.quote
        ? [{ key: 'preview', service, quote: estimate.quote }]
        : []
  ).filter((s) => s.quote)

  // Summed across the pickup for display only — each order earns at most one
  // free shirt, and the promo is never computed across the group.
  const freeShirtOffCents = breakdownSections.reduce(
    (sum, s) => sum + (s.quote?.freeShirt?.applied ? s.quote.freeShirt.amountCents : 0),
    0,
  )

  // Back to "nothing picked": the form is clean and Book now unlocks.
  function clearSelection() {
    setServiceId(null)
    setBags('1')
    setAddonQty({})
    setGarmentQty({})
    setFieldErrors((e) => ({ ...e, service: undefined, bags: undefined }))
  }

  // Drop the current selection into the pickup and reset the form for the
  // next service.
  function handleAddService() {
    if (!service) {
      setFieldErrors((e) => ({ ...e, service: 'Pick a service.' }))
      showToast('Pick a service first')
      return
    }
    if (membersGate) {
      setFieldErrors((e) => ({ ...e, service: 'Activate your Ghost Members plan first.' }))
      showToast('Activate your Ghost Members plan first')
      return
    }
    if (cartFull) { showToast('A pickup can carry up to 3 services'); return }
    setCart((cur) => [...cur, {
      key: `c${Date.now()}`,
      service,
      bags,
      lines: estimate?.lines || [],
      quote: estimate?.quote || null,
    }])
    // Reset for the next service — the taken one is filtered out below.
    clearSelection()
    showToast(`${service.name} added to your pickup`)
  }

  // ── Validation + submit ──────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [fieldErrors, setFieldErrors] = useState({})

  function validate() {
    const errs = {}
    // Booking validates the cart, not the form being composed — the current
    // selection is only a draft until "Add service" is pressed.
    if (cart.length === 0) errs.service = 'Add at least one service to your pickup.'
    // Booking with a service still selected would silently drop it — the one
    // thing the customer would notice only on the invoice.
    else if (service) errs.service = `Add ${service.name} to your pickup, or remove it with ×.`
    if (!pickupDate) errs.pickupDate = 'Pick a pickup date.'
    // Last line of defence for the Same-Day window. The picker already
    // blocks it and the effect above clamps it, but a date can also arrive
    // from state that predates adding Same-Day to the cart.
    else if (pickupWindow.max && pickupDate > pickupWindow.max) {
      errs.pickupDate = `Same-Day only runs today or tomorrow. Pick ${shortDate(pickupWindow.max)} or earlier, or book it as Wash & Fold.`
    } else if (pickupDate < pickupWindow.min) {
      errs.pickupDate = `That date has passed. The earliest pickup is ${shortDate(pickupWindow.min)}.`
    }
    if (!pickupSlot) errs.pickupSlot = 'Choose a pickup time window.'
    if (!addr.line1?.trim()) errs.addressLine1 = 'Street address is required.'
    if (!addr.city?.trim()) errs.city = 'City is required.'
    if (!addr.state?.trim()) errs.state = 'State is required.'
    if (!addr.zip?.trim()) errs.zip = 'ZIP code is required.'
    if (membersGate) errs.service = 'Activate your Ghost Members plan first.'
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  const [detailOpen, setDetailOpen] = useState(false)

  async function handleBook() {
    if (submitting) return
    setSubmitError('')
    if (!validate()) {
      if (fieldErrors.addressLine1 || fieldErrors.city || fieldErrors.state || fieldErrors.zip) {
        setEditingAddress(true)
      }
      showToast('Fix the highlighted fields to continue')
      return
    }
    setSubmitting(true)

    if (saveAsDefault && user?.id) {
      try {
        await upsertProfile(user.id, {
          address_line1: addr.line1 || '',
          address_line2: addr.line2 || '',
          city: addr.city || '',
          state: addr.state || '',
          zip: addr.zip || '',
          hotel_name: addr.isHotel ? (addr.hotelName || '') : '',
          room_number: addr.isHotel ? (addr.roomNumber || '') : '',
        })
        await refresh()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[order] profile address sync failed', err)
      }
    }

    const addressPayload = {
      address_line1: addr.line1 || '',
      address_line2: addr.line2 || '',
      city: addr.city || '',
      state: addr.state || '',
      zip: addr.zip || '',
      hotel_name: addr.isHotel ? (addr.hotelName || '') : '',
      room_number: addr.isHotel ? (addr.roomNumber || '') : '',
      notes: notes || '',
      pickup_date: pickupDate,
      pickup_slot: pickupSlot,
    }

    // ── Multi-service pickup: N orders, one transaction ─────────────────
    if (multiService) {
      let created
      try {
        created = await createPickupGroup(
          cart.map((c) => ({
            ...addressPayload,
            service: c.service?.key,
            service_id: c.service?.id,
            bags: c.bags,
            // Frozen at creation, same rule as the single-order path.
            pricing_zone_key: zone,
          })),
        )
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[order] group insert failed', err)
        const msg = [err?.message, err?.details, err?.hint].filter(Boolean).join(' · ')
          || 'Booking failed — please try again.'
        setSubmitError(msg)
        showToast(msg)
        setSubmitting(false)
        return
      }

      // Items, order by order — the RPC returns them in the order we sent.
      const itemSets = cart.map((c) => c.lines || [])
      await Promise.all(
        created.map((row, i) =>
          (itemSets[i]?.length
            ? insertOrderItems(row.id, itemSets[i]).catch((err) => {
                // eslint-disable-next-line no-console
                console.warn('[order] order_items insert failed', err)
              })
            : Promise.resolve()),
        ),
      )

      // One pickup ⇒ one alert, one SMS and one email, keyed on the first
      // order. Awaited for the same reason as the SMS: navigate() below can
      // abort in-flight fetches from this page.
      notifyNewOrder(created[0]?.id)
      // ONE purchase event for the whole pickup, at the group total — the
      // same number the bar showed. Never one event per order.
      trackPurchase({
        valueCents: cartTotalCents,
        orderNumber: created[0]?.order_number,
        services: cart.map((c) => c.service?.name).filter(Boolean),
      })
      try { await sendBooked(created[0]?.id) } catch { /* ignore */ }
      try { await sendSms(created[0]?.id, 'pickup_confirmed') } catch { /* ignore */ }

      navigate(`/confirmation?id=${encodeURIComponent(created[0]?.order_number || created[0]?.id)}`)
      return
    }

    // ── Single service: the cart holds exactly one entry ─────────────────
    const only = cart[0]
    let record
    try {
      record = await saveOrder({
        service: only?.service?.key,
        service_id: only?.service?.id,
        bags: only?.bags,
        pickupDate,
        pickupSlot,
        returnDate: effectiveReturn.date || null,
        returnSlot: effectiveReturn.slot || null,
        returnDeferred: !!effectiveReturn.deferred,
        notes,
        address: {
          street: addr.line1 || '',
          apt: addr.line2 || '',
          city: addr.city || '',
          state: addr.state || '',
          zip: addr.zip || '',
          hotelName: addr.isHotel ? (addr.hotelName || '') : '',
          roomNumber: addr.isHotel ? (addr.roomNumber || '') : '',
        },
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[order] insert failed', err)
      const parts = [err?.message, err?.details, err?.hint].filter(Boolean)
      const msg = parts.join(' · ') || 'Booking failed — please try again.'
      setSubmitError(msg)
      showToast(msg)
      setSubmitting(false)
      return
    }

    // Save order_items linked to this new order.
    if (only?.lines?.length > 0) {
      try {
        await insertOrderItems(record.id, only.lines)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[order] order_items insert failed', err)
      }
    }

    // Same single purchase event as the group path — one pickup, one event.
    trackPurchase({
      valueCents: cartTotalCents,
      orderNumber: record.order_number,
      services: cart.map((c) => c.service?.name).filter(Boolean),
    })

    // Fire the "pickup confirmed" SMS (spec §7quater Phase 1). We AWAIT it
    // even though the SMS itself is not critical — React Router's navigate()
    // can abort in-flight fetches from the previous page, so a bare fire-
    // and-forget would silently die.
    try { await sendBooked(record.id) } catch { /* ignore */ }
    try { await sendSms(record.id, 'pickup_confirmed') } catch { /* ignore */ }

    navigate(`/confirmation?id=${encodeURIComponent(record.order_number || record.id)}`)
  }

  const summary = formatSummary(addr)
  const hasAnyAddr = !!(addr.line1 || addr.city || addr.state || addr.zip)
  const addonsCount = Object.values(addonQty).reduce((s, n) => s + (n > 0 ? 1 : 0), 0)
  const garmentsCount = Object.values(garmentQty).reduce((s, n) => s + n, 0)

  return (
    <Layout>
      <div className="px-5 pb-64 pt-5">
        <h1 className="font-bricolage text-[20px] font-extrabold text-navy">
          Book pickup 👋
        </h1>
        <p className="mt-1 text-[13px] text-ink-2">Free pickup &amp; delivery.</p>

        {/* 1. Pickup address ─────────────────────────────────────────── */}
        <div className="mt-4">
          <div className="field-label">📍 Pickup address</div>
          {!editingAddress && hasAnyAddr ? (
            <div className="flex items-start justify-between gap-3 rounded-[10px] border border-navy bg-white px-3 py-2.5">
              <div className="min-w-0 text-[12px] leading-tight text-navy">
                {addr.hotelName ? (
                  <>
                    <div className="font-bold">
                      {addr.hotelName}
                      {addr.roomNumber ? ` · Room ${addr.roomNumber}` : ''}
                    </div>
                    <div className="mt-0.5 text-ink-2">{summary || '—'}</div>
                  </>
                ) : (
                  <>
                    <div className="font-bold">Pickup at:</div>
                    <div className="mt-0.5">{summary || '—'}</div>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => setEditingAddress(true)}
                className="text-[11px] font-extrabold text-navy underline"
              >
                Change
              </button>
            </div>
          ) : (
            <AddressEditor
              addr={addr}
              setAddr={setAddr}
              setAddrField={setAddrField}
              fieldErrors={fieldErrors}
              hasAnyAddr={hasAnyAddr}
              saveAsDefault={saveAsDefault}
              setSaveAsDefault={setSaveAsDefault}
              addrNote={addrNote}
              onHotelPick={handleHotelPick}
              onAddressPick={handleAddressPick}
              done={() => setEditingAddress(false)}
            />
          )}
        </div>

        {/* 2. Service ────────────────────────────────────────────────── */}
        <div className="mt-6">
          <div className="field-label">
            {cart.length === 0 ? 'Service' : 'Add another service'}
            {cart.length > 0 && <span className="text-ink-3"> (optional)</span>}
          </div>
          {servicesError ? (
            <p className="text-[12px] font-bold text-red">{servicesError}</p>
          ) : cartFull ? (
            <p className="rounded-lg border border-line bg-[#F9FAFB] px-3 py-2.5 text-[12px] text-ink-2">
              A pickup can carry up to 3 services. Book this one, then start another.
            </p>
          ) : visibleServices.length === 0 ? (
            <p className="rounded-lg border border-line bg-[#F9FAFB] px-3 py-2.5 text-[12px] text-ink-2">
              Every service is already in this pickup.
            </p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {visibleServices.map((s) => {
                const selected = serviceId === s.id
                const isMembers = s.key === 'members'
                const lockedForMembers = isMembers && !isMember
                return (
                  <ServiceRow
                    key={s.id}
                    service={s}
                    selected={selected}
                    memberUsage={
                      isMembers
                        ? {
                            used: Number(memberLbsUsed) || 0,
                            included: Number(s.monthly_included_lbs) || 0,
                          }
                        : null
                    }
                    lockedForMembers={lockedForMembers}
                    onSelect={() => {
                      if (lockedForMembers) {
                        navigate('/account') // subscription flow lives on Profile for now
                        return
                      }
                      userPickedService.current = true
                      setServiceId(s.id)
                    }}
                  />
                )
              })}
            </div>
          )}
          {fieldErrors.service && (
            <p className="mt-1 text-[10px] font-bold text-red">{fieldErrors.service}</p>
          )}

          {/* Running low on the cycle — information, not an offer. The
              prepaid top-up that used to sit here was removed on 2026-10-08:
              nobody ever bought any, and 25 cents a pound was never going to
              make someone reach for their card in advance. */}
          {lowOnLbs && (
            <div
              className="mt-2 rounded-[10px] p-3"
              style={{ background: '#0B2038', color: '#fff' }}
            >
              <div className="font-bricolage text-[13px] font-extrabold" style={{ color: '#F0DCA0' }}>
                {memberLbsLeft <= 0
                  ? 'Cycle full'
                  : `${memberLbsLeft} lb${memberLbsLeft === 1 ? '' : 's'} left this cycle`}
              </div>
              <div className="mt-0.5 text-[11px] text-white/75">
                {overagePerLb
                  ? `Anything beyond that is billed at $${overagePerLb.toFixed(2)}/lb on your next invoice.`
                  : 'Anything beyond that is billed on your next invoice.'}
              </div>
            </div>
          )}
        </div>

        {/* 3. Bags — hidden if per_item ─────────────────────────────── */}
        {service?.pricing_mode !== 'per_item' && (
          <div className="mt-6">
            <div className="field-label">How many bags</div>
            {/* Number on top, unit underneath: "4+ bags" on one line used to
                overflow a 375px screen and clip the last options. */}
            <div className="grid grid-cols-4 gap-1.5">
              {BAGS.map((b) => {
                const selected = bags === b.value
                return (
                  <button
                    type="button"
                    key={b.value}
                    onClick={() => setBags(b.value)}
                    aria-pressed={selected}
                    className={`flex min-h-[52px] flex-col items-center justify-center rounded-[10px] border-[1.5px] px-1 py-2 text-center transition ${
                      selected ? 'border-navy bg-navy text-white' : 'border-line bg-white text-navy'
                    }`}
                  >
                    <span className="font-bricolage text-[15px] font-extrabold leading-none">{b.value}</span>
                    <span className={`mt-0.5 text-[10px] leading-none ${selected ? 'text-white/75' : 'text-ink-2'}`}>
                      {b.value === '1' ? 'bag' : 'bags'}
                    </span>
                  </button>
                )
              })}
            </div>
            {fieldErrors.bags && (
              <p className="mt-1 text-[10px] font-bold text-red">{fieldErrors.bags}</p>
            )}
          </div>
        )}

        {/* 4. Pickup date + time + return summary ────────────────────── */}
        <div className="mt-6">
          <div className="field-label">Pickup</div>
          <PickupChips
            selected={pickupDate}
            onSelect={setPickupDate}
            sameDay={cartHasSameDay}
          />
          {fieldErrors.pickupDate && (
            <p className="mt-1 text-[10px] font-bold text-red">{fieldErrors.pickupDate}</p>
          )}

          {pickupDate && (
            <div className="mt-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-ink-3">
                Pickup time — {shortDate(pickupDate)}
              </div>
              {pickupSlots.length === 0 ? (
                <div className="mt-1.5 rounded-lg border border-line bg-[#F9FAFB] px-3 py-2.5 text-[12px] text-ink-2">
                  No more windows today — pick a later date.
                </div>
              ) : (
                <div className="mt-1.5 flex flex-col gap-1.5">
                  {pickupSlots.map((t) => (
                    <SlotRow
                      key={t.id}
                      title={t.title}
                      sub={t.sub}
                      selected={pickupSlot === t.id}
                      onClick={() => setPickupSlot(t.id)}
                    />
                  ))}
                </div>
              )}
              {fieldErrors.pickupSlot && (
                <p className="mt-1 text-[10px] font-bold text-red">{fieldErrors.pickupSlot}</p>
              )}
            </div>
          )}

          {/* Two services on one pickup ⇒ one trip back, once everything is
              done. No date is promised, and the customer sees that before
              they submit — not after. */}
          {pickupSlot && multiService && (
            <div className="mt-3 rounded-[10px] border border-line bg-white px-3 py-2.5 text-[12px] leading-tight">
              <div className="font-bold text-navy">Return confirmed when everything is ready</div>
              <div className="mt-0.5 text-ink-2">
                Your {cart.length} services come back together, in one trip.
              </div>
            </div>
          )}

          {pickupSlot && returnService && (
            <div className="mt-3 flex items-center justify-between rounded-[10px] border border-line bg-white px-3 py-2.5">
              <div className="min-w-0 text-[12px] leading-tight">
                <div className="font-bold text-navy">
                  Return · {effectiveReturn.deferred ? turnaroundLabel(returnService) : shortDate(effectiveReturn.date)}
                </div>
                <div className="mt-0.5 text-ink-2">{effectiveReturn.label}</div>
              </div>
              {!effectiveReturn.deferred && (
                <button
                  type="button"
                  onClick={() => setEditingReturn((v) => !v)}
                  className="text-[11px] font-extrabold text-navy underline"
                >
                  {editingReturn ? 'Done' : 'Change'}
                </button>
              )}
            </div>
          )}

          {editingReturn && returnService && !effectiveReturn.deferred && (
            <ReturnEditor
              service={returnService?.key}
              pickupDate={pickupDate}
              pickupSlot={pickupSlot}
              minDate={minReturnDate(returnService?.key, pickupDate, pickupSlot)}
              value={returnOverride || (autoReturn.date ? { date: autoReturn.date, slot: autoReturn.slot } : null)}
              onChange={(next) => setReturnOverride(next)}
              onReset={() => { setReturnOverride(null); setEditingReturn(false) }}
            />
          )}
        </div>

        {/* 5. Extras — collapsed by default ──────────────────────────── */}
        <div className="mt-6">
          <div className="field-label">Extras <span className="text-ink-3">(optional)</span></div>

          {(
            <details
              open={dryOpen || service?.key === 'dry_cleaning'}
              onToggle={(e) => setDryOpen(e.currentTarget.open)}
              className="overflow-hidden rounded-[10px] border border-line bg-white"
            >
              <summary className="drawer-summary flex cursor-pointer items-center justify-between px-3 py-2.5 text-[13px] font-extrabold text-navy">
                <span>
                  {service?.key === 'dry_cleaning' ? 'Choose your items' : 'Add individual items'}
                </span>
                <span className="flex items-center gap-2 text-[11px] text-ink-2">
                  {garmentsCount + addonsCount > 0 ? `${garmentsCount + addonsCount} selected` : ''}
                  <span aria-hidden className="drawer-caret text-[14px] leading-none text-ink-2">▾</span>
                </span>
              </summary>
              <div className="border-t border-line p-3">
                {garments.length > 0 || addons.length > 0 ? (
                  <GarmentList
                    items={garments}
                    qty={garmentQty}
                    setQty={setGarmentQty}
                    addons={addons}
                    addonQty={addonQty}
                    setAddonQty={setAddonQty}
                    bags={bags}
                  />
                ) : (
                  <p className="text-[11px] italic text-ink-3">
                    Loading items…
                  </p>
                )}
              </div>
            </details>
          )}
        </div>

        {/* 6. Notes ──────────────────────────────────────────────────── */}
        <div className="mt-6">
          <div className="field-label">Notes <span className="text-ink-3">(optional)</span></div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Hotel, room number, gate code, delivery instructions, stains to treat…"
            className="w-full resize-none rounded-lg border-[1.5px] border-line bg-white px-3 py-2.5 text-[12px] text-ink placeholder:text-ink-3 focus:border-navy focus:outline-none"
            style={{ minHeight: 64 }}
          />
        </div>

        {/* 7. What's in this pickup so far ───────────────────────────── */}
        {cart.length > 0 && (
          <div className="mt-6">
            <div className="field-label">Your pickup</div>
            <ul className="space-y-1.5">
              {cart.map((c) => (
                <li
                  key={c.key}
                  className="flex items-center justify-between gap-2 rounded-[10px] border-[1.5px] border-line bg-white px-3 py-2.5"
                >
                  <span className="min-w-0">
                    <span className="block font-bricolage text-[13px] font-extrabold text-navy">
                      {c.service?.name}
                    </span>
                    <span className="block text-[11px] text-ink-2">
                      {c.service?.pricing_mode === 'per_item'
                        ? `${(c.lines || []).reduce((s, l) => s + Number(l.quantity || 0), 0)} item(s)`
                        : `${c.bags} bag${c.bags === '1' ? '' : 's'}`}
                      {(c.lines || []).length > 0 && c.service?.pricing_mode !== 'per_item'
                        ? ` · ${c.lines.length} extra${c.lines.length > 1 ? 's' : ''}`
                        : ''}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="font-bricolage text-[13px] font-extrabold text-navy">
                      {fmtMoney(c.quote?.totalCents || 0)}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFromCart(c.key)}
                      aria-label={`Remove ${c.service?.name}`}
                      className="flex h-7 w-7 items-center justify-center rounded-md border border-line text-[15px] font-extrabold text-red hover:bg-red/10"
                    >
                      ×
                    </button>
                  </span>
                </li>
              ))}
            </ul>
            {multiService && (
              <p className="mt-2 rounded-lg bg-aqua-soft px-3 py-2 text-[11px] leading-relaxed text-navy">
                Each service is priced on its own — two services means two minimums.
                Everything comes back together, so the return date is confirmed once
                the whole pickup is ready.
              </p>
            )}
          </div>
        )}

        {submitError && (
          <p className="mt-3 whitespace-pre-wrap rounded-lg bg-red/10 px-3 py-2 text-[12px] font-bold text-red">
            {submitError}
          </p>
        )}

        {membersGate && (
          <div className="mt-4 rounded-xl border-[1.5px] border-gold bg-gold-soft p-4 text-center">
            <div className="font-bricolage text-[14px] font-extrabold text-navy">
              Ghost Members required
            </div>
            <p className="mt-1 text-[11px] leading-relaxed text-navy/80">
              Booking with the Ghost Members plan requires an active membership.
              $95/mo · 3 bags included · Cancel anytime.
            </p>
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="mt-3 w-full rounded-xl bg-gold px-4 py-3 font-bricolage text-[13px] font-extrabold text-white"
            >
              Become a Ghost Member
            </button>
          </div>
        )}
      </div>

      {/* 8. Sticky bottom bar — sits ABOVE the CustomerNav (56 px).
          Empty pickup ⇒ a single "Add service" button. Once something is in
          the cart it splits: keep adding, or book. */}
      {(estimate?.quote || cart.length > 0) && !membersGate && (
        <div
          className="fixed inset-x-0 z-40 mx-auto max-w-md border-t border-line bg-white p-3 shadow-[0_-6px_20px_rgba(11,32,56,0.06)]"
          style={{ bottom: 'calc(56px + env(safe-area-inset-bottom))' }}
        >
          {/* One line per service, then the total. Two services = two flat
              rates; nothing is merged or discounted. */}
          {cart.length > 1 && (
            <div className="mb-2 border-b border-line pb-2 text-[11px]">
              {cart.map((c) => (
                <div key={c.key} className="flex items-center justify-between">
                  <span className="text-ink-2">{c.service?.name}</span>
                  <span className="font-bold text-navy">{fmtMoney(c.quote?.totalCents || 0)}</span>
                </div>
              ))}
            </div>
          )}

          {/* The breakdown below is collapsed behind a button, so a promo
              that only lived in there was a promo nobody saw. This line sits
              in the always-visible bar: the offer has to be legible while
              the customer is deciding, not one click away. */}
          {freeShirtOffCents > 0 && (
            <div
              className="mb-2 flex items-center justify-between rounded-md px-2 py-1 text-[11px]"
              style={{ background: '#E9F9EF', border: '1px solid #B6E7C7' }}
            >
              <span className="font-extrabold" style={{ color: '#065F46' }}>
                Free shirt — cheapest one on us
              </span>
              <span className="font-extrabold" style={{ color: '#065F46' }}>
                −{fmtMoney(freeShirtOffCents)}
              </span>
            </div>
          )}

          <div className="flex items-baseline justify-between">
            <div>
              <div className="font-bricolage text-[18px] font-extrabold text-navy">
                {fmtMoney(barTotalCents)}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-ink-3">
                {breakdownSections.length > 1 ? 'estimated total' : 'estimated'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setDetailOpen((v) => !v)}
              className="text-[11px] font-extrabold text-navy underline"
            >
              {detailOpen ? 'Hide breakdown' : 'Breakdown'}
            </button>
          </div>

          {detailOpen && (
            <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-line bg-[#F9FAFB] p-2 text-[11px]">
              {breakdownSections.length === 0 && (
                <div className="text-ink-3">No charges yet — pick a service.</div>
              )}
              {breakdownSections.map((s, idx) => (
                <div key={s.key} className={idx > 0 ? 'mt-2 border-t border-line pt-2' : ''}>
                  {breakdownSections.length > 1 && (
                    <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-ink-3">
                      {s.service?.name}
                    </div>
                  )}
                  {s.quote.baseCents > 0 && (
                    <BreakdownRow
                      label={`${s.service?.name || 'Service'}${s.service?.included_lbs ? ` — up to ${s.service.included_lbs} lbs` : ''}`}
                      cents={s.quote.baseCents}
                    />
                  )}
                  {(s.quote.lines || []).map((l, i) => {
                    // Show the shirts at full price and the promo on its own
                    // line below. Netting it into the line would give the
                    // right total with nothing explaining it — and the whole
                    // point of the offer is that the customer sees it here,
                    // while deciding.
                    const shown = Number(l.lineTotalCents) + Number(l.freeShirtCents || 0)
                    return shown > 0 && (
                      <BreakdownRow
                        key={i}
                        label={`${l.name_snapshot}${l.quantity > 1 ? ` × ${l.quantity}` : ''}`}
                        cents={shown}
                      />
                    )
                  })}
                  {s.quote.freeShirt?.applied && (
                    <BreakdownRow
                      label={`Free shirt — ${s.quote.freeShirt.shirtCount} shirts promo`}
                      cents={-s.quote.freeShirt.amountCents}
                      accent
                    />
                  )}
                  {s.quote.handlingCents > 0 && (
                    <BreakdownRow
                      label={`Handling & transportation ${Number(s.service?.handling_fee_pct || 0)}%`}
                      cents={s.quote.handlingCents}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {cart.length === 0 ? (
            <button
              type="button"
              onClick={handleAddService}
              disabled={!service}
              className={`mt-2 w-full rounded-xl bg-navy px-5 py-3 font-bricolage text-[14px] font-extrabold text-white ${service ? '' : 'opacity-50'}`}
            >
              Add service
            </button>
          ) : (
            <>
              {/* A picked-but-not-added service blocks Book now, so it needs
                  an obvious way out right next to the button that's greyed. */}
              {hasPending && (
                <div className="mt-2 flex items-center justify-between gap-2 rounded-[10px] border-[1.5px] border-navy bg-white px-3 py-2">
                  <span className="min-w-0 truncate text-[12px] font-bold text-navy">
                    {service.name}
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="font-bricolage text-[13px] font-extrabold text-navy">
                      {fmtMoney(estimate?.quote?.totalCents || 0)}
                    </span>
                    <button
                      type="button"
                      onClick={clearSelection}
                      aria-label={`Remove ${service.name} from this pickup`}
                      className="flex h-7 w-7 items-center justify-center rounded-md border border-line text-[15px] font-extrabold text-red hover:bg-red/10"
                    >
                      ×
                    </button>
                  </span>
                </div>
              )}
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={handleAddService}
                  disabled={!service || cartFull}
                  title={cartFull ? 'Up to 3 services per pickup' : undefined}
                  className={`flex-1 rounded-xl px-3 py-3 font-bricolage text-[13px] font-extrabold ${
                    hasPending && !cartFull
                      ? 'bg-navy text-white'
                      : 'border-[1.5px] border-navy bg-white text-navy opacity-40'
                  }`}
                >
                  + Add another
                </button>
                <button
                  type="button"
                  onClick={handleBook}
                  disabled={submitting || hasPending}
                  title={hasPending ? 'Add the service you picked, or remove it' : undefined}
                  className={`flex-1 rounded-xl bg-navy px-3 py-3 font-bricolage text-[13px] font-extrabold text-white ${
                    submitting || hasPending ? 'opacity-40' : ''
                  }`}
                >
                  {submitting ? 'Booking…' : 'Book now'}
                </button>
              </div>
            </>
          )}
          <p className="mt-1.5 text-center text-[10px] leading-relaxed text-ink-3">
            {cart.length === 0
              ? 'Add your service to the pickup — you can add more than one.'
              : hasPending
                ? `Add ${service.name} to your pickup, or remove it with ×, then book.`
                : 'No payment now. We weigh your laundry, then you get the invoice by email.'}
          </p>
        </div>
      )}
    </Layout>
  )
}

// ══════════════════════════════════════════════════════════════════════
// Sub-components
// ══════════════════════════════════════════════════════════════════════

function ServiceRow({ service, selected, memberUsage, lockedForMembers, onSelect }) {
  const isDry = service.key === 'dry_cleaning'
  const isMembers = service.key === 'members'
  const priceStr = isDry
    ? 'Variable / by items'
    : isMembers
    ? 'Included'
    : `$${((service.base_price_cents || 0) / 100).toFixed(0)}`
  // Members: never clamp silently — surface overage instead of showing a
  // misleading "0 lbs left" or "3 lbs left" while the member is already
  // past the cycle allowance.
  let subLine = ''
  if (isMembers) {
    if (memberUsage) {
      const left = Math.max(0, memberUsage.included - memberUsage.used)
      const over = Math.max(0, memberUsage.used - memberUsage.included)
      subLine = over > 0
        ? `Cycle full · ${over.toFixed(1)} lbs overage`
        : `${left} lbs left this cycle`
    }
  } else if (isDry) {
    subLine = 'per-item pricing'
  } else {
    subLine = `up to ${service.included_lbs} lbs`
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex items-center justify-between rounded-[10px] border-[1.5px] px-3 py-3 text-left transition ${
        selected
          ? 'border-navy bg-navy text-white'
          : 'border-line bg-white text-navy hover:border-navy/50'
      }`}
    >
      <div className="min-w-0 pr-3">
        <div className="flex items-center gap-2">
          <span className={`flex h-[18px] w-[18px] items-center justify-center rounded-full border-[2px] ${
            selected ? 'border-white bg-white' : 'border-line-strong bg-white'
          }`}>
            {selected && <span className="h-2 w-2 rounded-full bg-navy" />}
          </span>
          <span className="font-bricolage text-[13px] font-extrabold">{service.name}</span>
          {lockedForMembers && (
            <span className="ml-1 rounded bg-gold px-1.5 py-0.5 text-[8px] font-extrabold uppercase tracking-wider text-white">Members only</span>
          )}
        </div>
        {service.description && (
          <div className={`mt-1 text-[11px] ${selected ? 'text-white/70' : 'text-ink-2'}`}>
            {service.description}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 text-right">
        <div className={`font-bricolage text-[15px] font-extrabold ${selected ? 'text-white' : 'text-navy'}`}>
          {priceStr}
        </div>
        {subLine && (
          <div className={`mt-0.5 text-[10px] ${selected ? 'text-white/70' : 'text-ink-2'}`}>
            {subLine}
          </div>
        )}
      </div>
    </button>
  )
}

// `sameDay` locks the picker to today/tomorrow. The chips used to know
// nothing about the service, so "Pick a date" opened an unbounded calendar
// and a Same-Day could be booked for any month you cared to page to.
function PickupChips({ selected, onSelect, sameDay = false }) {
  const today = isoDate(new Date())
  const tomorrow = addDays(today, 1)
  const [modalOpen, setModalOpen] = useState(false)
  const { min, max } = pickupWindowFor(sameDay ? 'same_day' : null)

  // Today is offered only while it is still bookable — for Same-Day that
  // means the morning slot hasn't passed, since the return is that evening.
  const todayOffered = min <= today
  const customAllowed = !sameDay

  return (
    <div className="flex flex-wrap gap-1.5">
      {/* "Today", not "Tonight": this chip picks a DAY. Labelling it by a
          time of day was wrong for a morning pickup — Same-Day's is always
          a morning one — and for a morning Wash & Fold too.
          Ce commentaire était à l'intérieur de `{todayOffered && ( … )}`,
          ce que JSX refuse : le build de l'app était cassé. */}
      {todayOffered && (
        <Chip label="Today" active={selected === today} onClick={() => onSelect(today)} />
      )}
      <Chip label="Tomorrow" active={selected === tomorrow} onClick={() => onSelect(tomorrow)} />
      {customAllowed && (
        <Chip
          label={selected && selected !== today && selected !== tomorrow ? shortDate(selected) : 'Pick a date'}
          active={selected && selected !== today && selected !== tomorrow}
          dashed
          onClick={() => setModalOpen(true)}
        />
      )}
      {sameDay && (
        <p className="mt-1 w-full text-[11px] leading-relaxed text-ink-2">
          Same-Day runs on a morning pickup and comes back that evening, so it
          can only be booked for {todayOffered ? 'today or tomorrow' : 'tomorrow'}.
          Need a later date? Book it as Wash &amp; Fold instead.
        </p>
      )}
      {modalOpen && customAllowed && (
        <div className="w-full">
          <DateInput
            label="Pickup date"
            placeholder="Pick a pickup date"
            selected={selected}
            onSelect={(iso) => { onSelect(iso); setModalOpen(false) }}
            minDate={min}
            maxDate={max}
            accent="navy"
          />
        </div>
      )}
    </div>
  )
}

function Chip({ label, active, onClick, dashed }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-[10px] px-3 py-2 text-[12px] font-extrabold transition ${
        active
          ? 'border-[1.5px] border-navy bg-navy text-white'
          : dashed
          ? 'border-[1.5px] border-dashed border-line-strong bg-white text-ink-2 hover:text-navy hover:border-navy'
          : 'border-[1.5px] border-line bg-white text-navy hover:border-navy/50'
      }`}
    >
      {label}
    </button>
  )
}

function SlotRow({ title, sub, selected, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-between rounded-lg border-[1.5px] px-3 py-2.5 text-left ${
        selected ? 'border-navy bg-navy text-white' : 'border-line bg-white text-navy'
      } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      <div>
        <div className="font-bricolage text-[12px] font-extrabold">{title}</div>
        <div className={`text-[11px] ${selected ? 'text-white/70' : 'text-ink-2'}`}>{sub}</div>
      </div>
      <div className={`flex h-4 w-4 items-center justify-center rounded-full border-[2px] ${
        selected ? 'border-white bg-white' : 'border-line-strong bg-white'
      }`}>
        {selected && <span className="h-1.5 w-1.5 rounded-full bg-navy" />}
      </div>
    </button>
  )
}

function ReturnEditor({ service, pickupDate, pickupSlot, minDate, value, onChange, onReset }) {
  const date = value?.date || ''
  const slot = value?.slot || ''
  const allowed = allowedReturnSlots(service, pickupDate, pickupSlot, date)

  function setDate(iso) {
    const next = allowedReturnSlots(service, pickupDate, pickupSlot, iso)
    const nextSlot = next.includes(slot) ? slot : (next[0] || '')
    onChange({ date: iso, slot: nextSlot })
  }

  return (
    <div className="mt-2 rounded-[10px] border border-line bg-white p-3">
      <div className="field-label">Return date</div>
      <DateInput
        label="Return date"
        placeholder="Pick a return date"
        selected={date}
        onSelect={setDate}
        minDate={minDate || undefined}
        accent="navy"
      />
      {date && (
        <>
          <div className="field-label mt-3">Return time — {shortDate(date)}</div>
          <div className="flex flex-col gap-1.5">
            {['morning', 'evening'].map((k) => {
              const s = SLOTS[k]
              const enabled = allowed.includes(k)
              return (
                <SlotRow
                  key={k}
                  title={s.title}
                  sub={s.sub}
                  selected={slot === k}
                  disabled={!enabled}
                  onClick={() => enabled && onChange({ date, slot: k })}
                />
              )
            })}
          </div>
        </>
      )}
      <button
        type="button"
        onClick={onReset}
        className="mt-3 w-full rounded-lg border border-line bg-white px-3 py-2 text-[11px] font-bold text-ink-2 hover:bg-[#F9FAFB]"
      >
        Reset to suggested
      </button>
    </div>
  )
}

function AddressEditor({
  addr, setAddr, setAddrField,
  fieldErrors, hasAnyAddr,
  saveAsDefault, setSaveAsDefault,
  addrNote, onHotelPick, onAddressPick,
  done,
}) {
  return (
    <div className="rounded-[10px] border border-line bg-white p-3">
      <label className="mb-2 flex items-center gap-2 text-[11px] text-navy">
        <input
          type="checkbox"
          checked={addr.isHotel}
          onChange={(e) => setAddr((cur) => ({
            ...cur,
            isHotel: e.target.checked,
            // Unchecking clears the hotel name so no ghost hotel stays on file.
            hotelName: e.target.checked ? cur.hotelName : '',
            roomNumber: e.target.checked ? cur.roomNumber : '',
          }))}
          className="h-4 w-4 rounded border-line"
        />
        I&apos;m staying in a hotel
      </label>

      {addr.isHotel && (
        <div className="mb-2">
          <AddressAutocomplete
            id="orderHotel"
            value={addr.hotelName}
            onChange={(v) => setAddrField('hotelName', v)}
            onPick={onHotelPick}
            kind="hotel"
            placeholder="Hotel name"
          />
        </div>
      )}

      <AddressAutocomplete
        id="orderStreet"
        value={addr.line1}
        onChange={(v) => setAddrField('line1', v)}
        onPick={onAddressPick}
        kind="address"
        placeholder="Street address *"
        autoComplete="street-address"
        required
        className={`field-input ${fieldErrors?.addressLine1 ? 'border-red' : ''}`}
      />
      {fieldErrors?.addressLine1 && <p className="mt-1 text-[10px] font-bold text-red">{fieldErrors.addressLine1}</p>}

      {addr.isHotel ? (
        <input
          className="field-input mt-2"
          placeholder="Room number"
          value={addr.roomNumber}
          onChange={(e) => setAddrField('roomNumber', e.target.value)}
        />
      ) : (
        <input
          className="field-input mt-2"
          placeholder="Apt / Suite (optional)"
          value={addr.line2}
          onChange={(e) => setAddrField('line2', e.target.value)}
        />
      )}

      <div className="mt-2 grid grid-cols-2 gap-2">
        <input className={`field-input ${fieldErrors?.city ? 'border-red' : ''}`} placeholder="City *" value={addr.city} onChange={(e) => setAddrField('city', e.target.value)} />
        <input className={`field-input ${fieldErrors?.state ? 'border-red' : ''}`} placeholder="State *" value={addr.state} onChange={(e) => setAddrField('state', e.target.value)} />
      </div>
      <input className={`field-input mt-2 ${fieldErrors?.zip ? 'border-red' : ''}`} placeholder="ZIP *" value={addr.zip} onChange={(e) => setAddrField('zip', e.target.value)} />

      {addrNote?.text && (
        <p
          className={`mt-2 text-[11px] leading-relaxed ${addrNote.warn ? 'text-[#7A5E0C]' : 'text-ink-2'}`}
          style={addrNote.warn ? { background: '#FEF6DF', border: '1px solid #F0DCA0', padding: '6px 10px', borderRadius: 8 } : undefined}
        >
          {addrNote.text}
        </p>
      )}

      <label className="mt-3 flex items-center gap-2 text-[11px] text-navy">
        <input type="checkbox" checked={saveAsDefault} onChange={(e) => setSaveAsDefault(e.target.checked)} className="h-4 w-4 rounded border-line" />
        Save as my default address
      </label>
      {hasAnyAddr && (
        <button
          type="button"
          onClick={done}
          className="mt-2 w-full rounded-lg border border-line bg-white px-3 py-2 text-[11px] font-bold text-ink-2 hover:bg-[#F9FAFB]"
        >
          Use this address
        </button>
      )}
    </div>
  )
}

function AddonRow({ item, bags, checked, onToggle }) {
  const bagCount = bags === '4+' ? 4 : Math.max(1, parseInt(bags, 10) || 1)
  const priceLabel = item.unit === 'per_bag'
    ? `$${(item.price_cents / 100).toFixed(2)} × ${bagCount} bag${bagCount === 1 ? '' : 's'}`
    : `$${(item.price_cents / 100).toFixed(2)}${item.unit === 'per_lb' ? '/lb' : ''}`
  return (
    <label className="flex cursor-pointer items-center justify-between py-1.5">
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={checked} onChange={onToggle} className="h-4 w-4 rounded border-line" />
        <div>
          <div className="text-[12px] font-bold text-navy">{item.name}</div>
          {item.description && <div className="text-[10px] text-ink-2">{item.description}</div>}
        </div>
      </div>
      <div className="text-[11px] font-bold text-navy">{priceLabel}</div>
    </label>
  )
}

// Unified item picker — ONE dropdown, three bold groups.
//   - Fixed group order: Dry Cleaning → Bedding → Wash & Fold, so bedding is
//     always in the same place. Groups with nothing in them are skipped.
//   - Dry cleaning and bedding are counted (− / + stepper). Wash options are
//     per-lb / per-bag / per-order, so they stay on/off toggles.
//   - Chosen pieces surface in "Your pieces" ABOVE the list and drop out of
//     the list below (they'd be duplicated otherwise).
//   - Inventory stays OPTIONAL — the customer can book without picking
//     anything and we count at the shop.
const GROUP_ORDER = ['Dry Cleaning', 'Bedding', 'Wash & Fold']

// The group is DERIVED, not stored: addons are wash options, anything in a
// "Bedding…" category is bedding, every other garment is dry cleaning. A new
// catalog category therefore lands somewhere sensible without a code change.
function groupOf(it) {
  if (it.kind === 'addon') return 'Wash & Fold'
  if ((it.category || '').startsWith('Bedding')) return 'Bedding'
  return 'Dry Cleaning'
}

function GarmentList({ items, qty, setQty, addons = [], addonQty = {}, setAddonQty, bags }) {
  const [q, setQ] = useState('')
  const [group, setGroup] = useState('All')

  const all = useMemo(() => [...items, ...addons], [items, addons])

  const groupsPresent = useMemo(() => {
    const present = new Set(all.map(groupOf))
    return ['All', ...GROUP_ORDER.filter((g) => present.has(g))]
  }, [all])

  // Only counted pieces belong in "Your pieces" — a toggled wash option has
  // no quantity to show there.
  const chosen = items.filter((it) => (qty[it.id] || 0) > 0)
  const chosenIds = new Set(chosen.map((it) => it.id))

  const filtered = all.filter((it) => {
    if (chosenIds.has(it.id)) return false
    if (group !== 'All' && groupOf(it) !== group) return false
    if (q && !it.name.toLowerCase().includes(q.trim().toLowerCase())) return false
    return true
  })

  const groups = GROUP_ORDER
    .map((g) => ({ group: g, items: filtered.filter((it) => groupOf(it) === g) }))
    .filter((g) => g.items.length > 0)

  function bump(id, delta) {
    setQty((cur) => {
      const next = Math.max(0, (cur[id] || 0) + delta)
      return { ...cur, [id]: next }
    })
  }

  return (
    <div className="space-y-3">
      <p className="text-[11px] leading-relaxed text-ink-2">
        Not sure what you&apos;re sending? Just book — we count every piece at the shop
        and text you the total before we clean anything.
      </p>

      {chosen.length > 0 && (
        <div className="rounded-lg border border-navy bg-[#F9FAFB] p-3">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-navy">
            Your pieces
          </div>
          {chosen.map((it) => (
            <GarmentRow key={`c-${it.id}`} it={it} q={qty[it.id] || 0} bump={bump} note={rowNote(it)} />
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        {groupsPresent.map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => setGroup(g)}
            className={`rounded-full border px-2.5 py-1 text-[11px] font-extrabold ${group === g ? 'border-navy bg-navy text-white' : 'border-line bg-white text-navy'}`}
          >
            {g}
          </button>
        ))}
      </div>

      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search — e.g. shirt, sheet"
        className="field-input"
      />

      <div style={{ maxHeight: 280 }} className="overflow-y-auto rounded-lg border border-line">
        {groups.length === 0 && (
          <div className="p-3 text-[11px] italic text-ink-3">Nothing matches — try another search.</div>
        )}
        {groups.map((g) => (
          <div key={g.group} className="border-t border-line first:border-t-0">
            <div className="bg-[#F9FAFB] px-3 py-1.5 text-[12px] font-extrabold text-navy">
              {g.group}
            </div>
            <div className="px-3">
              {g.items.map((it) => (
                it.kind === 'addon' ? (
                  <AddonRow
                    key={it.id}
                    item={it}
                    bags={bags}
                    checked={(addonQty[it.id] || 0) > 0}
                    onToggle={() => setAddonQty((cur) => ({ ...cur, [it.id]: cur[it.id] ? 0 : 1 }))}
                  />
                ) : (
                  <GarmentRow key={it.id} it={it} q={qty[it.id] || 0} bump={bump} note={rowNote(it)} />
                )
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Men / Women used to be their own sub-headers. Now that the bold header is
// the group, that distinction has to ride on the row — otherwise the two
// "Pants" (Men $10, Women $10) are indistinguishable.
function rowNote(it) {
  const c = it.category || ''
  return c === 'Men' || c === 'Women' ? c : ''
}

// `accent` is for money coming OFF the bill — it should not read like one
// more thing being charged. fmtMoney has no sign convention, so negative
// amounts are written out here.
function BreakdownRow({ label, cents, accent = false }) {
  const negative = cents < 0
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className={accent ? 'font-bold text-green' : 'text-ink-2'}>{label}</span>
      <span className={accent ? 'font-bold text-green' : 'text-navy'}>
        {negative ? `−${fmtMoney(Math.abs(cents))}` : fmtMoney(cents)}
      </span>
    </div>
  )
}

function GarmentRow({ it, q, bump, note = '' }) {
  const sub = q * it.price_cents
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="min-w-0 pr-3">
        <div className="text-[12px] font-bold text-navy">{it.name}</div>
        <div className="text-[10px] text-ink-2">
          ${(it.price_cents / 100).toFixed(2)}{note ? ` · ${note}` : ''}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 rounded-lg border border-line bg-white">
          <button type="button" onClick={() => bump(it.id, -1)} className="h-7 w-7 text-[13px] font-bold text-navy disabled:opacity-40" disabled={q === 0}>−</button>
          <span className="w-5 text-center text-[12px] font-extrabold text-navy">{q}</span>
          <button type="button" onClick={() => bump(it.id, 1)} className="h-7 w-7 text-[13px] font-bold text-navy">+</button>
        </div>
        <div className="w-14 text-right text-[11px] font-extrabold text-navy">
          {sub > 0 ? `$${(sub / 100).toFixed(2)}` : ''}
        </div>
      </div>
    </div>
  )
}
